import JSZip from "jszip";

import { Api } from "@penn/web-common";
import { unwrap } from "@penn/web-common";

const UPLOAD_TIMEOUT_MS = 300_000;

// General "Upload files" attachments on an application (NEW#3, POC parity).
// svc-06-quotation via gateway /v1/applications/:applicationId/files. Separate
// from the 3D STEP upload (svc-09); these are arbitrary supporting documents.

export interface ApplicationAttachment {
  fileId: string;
  applicationId: string;
  fileName: string;
  kind: "attachment";
  mimeType: string | null;
  sizeBytes: number | null;
  uploadedAt: string | null;
  uploadedByEmail: string | null;
}

export async function listAttachments(
  applicationId: string,
): Promise<ApplicationAttachment[]> {
  const res = await Api.get(
    `/v1/applications/${encodeURIComponent(applicationId)}/files`,
  );
  return unwrap<ApplicationAttachment[]>(res.data);
}

export async function uploadAttachment(
  applicationId: string,
  file: File,
): Promise<ApplicationAttachment> {
  const form = new FormData();
  form.append("file", file);
  const res = await Api.post(
    `/v1/applications/${encodeURIComponent(applicationId)}/files`,
    form,
    { timeout: UPLOAD_TIMEOUT_MS },
  );
  return unwrap<ApplicationAttachment>(res.data);
}

// Sends the one HubSpot submission for a saved application. Best-effort on the
// server, so a failure here must never block the save.
export async function finalizeApplicationCrm(
  applicationId: string,
): Promise<void> {
  await Api.post(
    `/v1/applications/${encodeURIComponent(applicationId)}/files/finalize`,
  );
}

export async function getAttachmentDownloadUrl(
  applicationId: string,
  fileId: string,
): Promise<{ url: string; fileName: string }> {
  const res = await Api.get(
    `/v1/applications/${encodeURIComponent(applicationId)}/files/${encodeURIComponent(fileId)}/download`,
  );
  return unwrap<{ url: string; fileName: string }>(res.data);
}

export async function deleteAttachment(
  applicationId: string,
  fileId: string,
): Promise<void> {
  await Api.delete(
    `/v1/applications/${encodeURIComponent(applicationId)}/files/${encodeURIComponent(fileId)}`,
  );
}

function triggerBlobDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** De-duplicating filename helper: "a.pdf", "a (1).pdf", … so nothing overwrites. */
function makeUniqueNamer(): (raw: string) => string {
  const usedNames = new Map<string, number>();
  return (raw: string): string => {
    const name = raw || "file";
    const seen = usedNames.get(name) ?? 0;
    usedNames.set(name, seen + 1);
    if (seen === 0) return name;
    const dot = name.lastIndexOf(".");
    return dot > 0
      ? `${name.slice(0, dot)} (${seen})${name.slice(dot)}`
      : `${name} (${seen})`;
  };
}

/**
 * C6 (POC parity "Download all"): bundle every
 * attachment on a SAVED application into a single ZIP. The browser fetches each
 * presigned S3 blob and zips them client-side (the cad3d bucket grants GET CORS
 * to the web origins so the cross-origin fetch succeeds). Duplicate file names
 * are suffixed so nothing is overwritten. Returns the file count.
 */
export async function downloadAllAttachments(
  applicationId: string,
  zipName = "attachments.zip",
): Promise<number> {
  const items = await listAttachments(applicationId);
  if (items.length === 0) return 0;

  const zip = new JSZip();
  const uniqueName = makeUniqueNamer();

  for (const item of items) {
    const { url, fileName } = await getAttachmentDownloadUrl(
      applicationId,
      item.fileId,
    );
    const res = await fetch(url);
    if (!res.ok) {
      throw new Error(`Failed to download "${fileName}" (${res.status}).`);
    }
    zip.file(uniqueName(fileName || item.fileName), await res.blob());
  }

  const archive = await zip.generateAsync({ type: "blob" });
  triggerBlobDownload(archive, zipName);
  return items.length;
}

/**
 * Download a SINGLE file that is still STAGED in memory on the new-application
 * popup (C2 — not yet uploaded). A `File` is a `Blob`, so we hand it straight to
 * the browser's download path — no network, no S3. Mirrors the per-file
 * "Download" the saved-application AttachmentsPanel offers.
 */
export function downloadStagedFile(file: File): void {
  triggerBlobDownload(file, file.name || "file");
}

/**
 * "Download all" for files that are still STAGED in memory on the new-application
 * popup (C2 — not yet uploaded, so there is no server copy). The File objects are
 * already in-browser, so we zip them directly with no network round-trip (and no
 * S3 CORS dependency). Returns the file count.
 */
export async function downloadFilesAsZip(
  files: File[],
  zipName = "attachments.zip",
): Promise<number> {
  if (files.length === 0) return 0;
  const zip = new JSZip();
  const uniqueName = makeUniqueNamer();
  for (const file of files) {
    zip.file(uniqueName(file.name || "file"), file);
  }
  const archive = await zip.generateAsync({ type: "blob" });
  triggerBlobDownload(archive, zipName);
  return files.length;
}
